import React from 'react';
import { Action, withInject } from '@piwikpro/platform';
import {
  Text,
  Tooltip,
  Icon,
  Stack,
  withTranslation,
} from '@piwikpro/ui-components';
import { TrackerSettings, TrackerSettingsResponse } from '../../../TrackerSettings';

export interface StripSiteSearchQueryParamsProps {
  appId: string
  trackerSettings: TrackerSettings
  globalTrackerSettings: TrackerSettingsResponse
  appTrackerSettings: TrackerSettingsResponse
  t: (key: string) => string
}

export interface StripSiteSearchQueryParamsState {
  useGlobals: boolean
}

interface FormValues {
  'strip_site_search_query_parameters': string[]
  'strip_site_search_query_parameters_global': string[]
}

class StripSiteSearchQueryParams extends React.Component<
StripSiteSearchQueryParamsProps,
StripSiteSearchQueryParamsState
> {
  static readonly fieldName = 'strip_site_search_query_parameters';

  constructor(props: StripSiteSearchQueryParamsProps) {
    super(props);
    const { appTrackerSettings } = props;

    this.state = {
      useGlobals:
        appTrackerSettings.attributes?.[StripSiteSearchQueryParams.fieldName] === undefined,
    };

    this.handleSubmitSwitcher = this.handleSubmitSwitcher.bind(this);
    this.handleSubmitCheckbox = this.handleSubmitCheckbox.bind(this);
  }

  async handleSubmitSwitcher(
    values: FormValues,
    { setFieldValue, setSubmitting } : { setFieldValue: Function, setSubmitting: Function },
  ) {
    const { trackerSettings, appId } = this.props;

    try {
      await trackerSettings.updateSettingsForApp(appId, values);
    } catch (err) {
      setFieldValue(
        StripSiteSearchQueryParams.fieldName,
        !values[StripSiteSearchQueryParams.fieldName],
        false,
      );
    }

    setSubmitting(false);
  }

  async handleSubmitCheckbox(
    values: FormValues,
    { setFieldValue, setSubmitting } : { setFieldValue: Function, setSubmitting: Function },
  ) {
    const { trackerSettings, globalTrackerSettings, appId } = this.props;

    try {
      if (values.strip_site_search_query_parameters_global) {
        await trackerSettings.deleteSettingsForApp(appId, StripSiteSearchQueryParams.fieldName);
      } else {
        await trackerSettings.updateSettingsForApp(appId, {
          [StripSiteSearchQueryParams.fieldName]:
            globalTrackerSettings.attributes[StripSiteSearchQueryParams.fieldName],
        });

        setFieldValue(
          StripSiteSearchQueryParams.fieldName,
          globalTrackerSettings.attributes[StripSiteSearchQueryParams.fieldName],
          false,
        );
      }
    } catch (err) {
      setFieldValue(
        'strip_site_search_query_parameters_global',
        !values.strip_site_search_query_parameters_global,
        false,
      );
      this.setState({
        useGlobals: !values.strip_site_search_query_parameters_global,
      });

      setSubmitting(false);

      return;
    }

    this.setState({
      useGlobals: Boolean(values.strip_site_search_query_parameters_global),
    });

    setSubmitting(false);
  }

  render() {
    const {
      appTrackerSettings,
      globalTrackerSettings,
      t,
    } = this.props;
    const { useGlobals } = this.state;

    return (
      <Stack vertical>
        <Stack.Item fill>
          <Stack wrap verticalAlignment="center">
            <Stack.Item fill>
              <Stack vertical spacing="narrow">
                <Stack.Item>
                  <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">{t('apps.details.settings.tracking.strip-site-search-query-parameters.title')}</Text>
                </Stack.Item>
                <Stack.Item>
                  <Text as="p">{t('apps.details.settings.tracking.strip-site-search-query-parameters.description')}</Text>
                </Stack.Item>
              </Stack>
            </Stack.Item>
            <Stack.Item>
              <Action.AutoSavedSwitcherControl
                id="app-settings-tracking-strip-site-search-query-parameters"
                initialValue={
                  useGlobals
                    ? globalTrackerSettings.attributes[StripSiteSearchQueryParams.fieldName]
                    : appTrackerSettings.attributes[StripSiteSearchQueryParams.fieldName]
                }
                label={t('apps.details.settings.tracking.strip-site-search-query-parameters.title')}
                info={t('apps.details.settings.tracking.strip-site-search-query-parameters.description')}
                name={StripSiteSearchQueryParams.fieldName}
                onSubmit={this.handleSubmitSwitcher}
                disabled={useGlobals}
                disabledActionTooltipText={t('app.details.settings.general.use-global-settings.block-reason')}
              />
            </Stack.Item>
          </Stack>
        </Stack.Item>
        <Stack.Item>
          <Stack spacing="narrow">
            <Stack.Item>
              <Action.AutoSavedCheckboxControl
                id="app-settings-strip-site-search-query-parameters-use-globals"
                onSubmit={this.handleSubmitCheckbox}
                initialValue={useGlobals}
                label={t('app.details.settings.general.use-global-settings.label')}
                name="strip_site_search_query_parameters_global"
              />
            </Stack.Item>
            <Stack.Item>
              <Tooltip content={t('app.details.settings.general.use-global-settings.tooltip') as any}>
                <div>
                  <Icon name="info-circle" />
                </div>
              </Tooltip>
            </Stack.Item>
          </Stack>
        </Stack.Item>
      </Stack>
    );
  }
}

export default withInject({
  trackerSettings: 'TrackerSettingsCrate.tracker',
})(withTranslation(['administration'])(StripSiteSearchQueryParams as any));
